import { z } from 'zod';
import { MEDIA_KINDS, TARGET_TYPES } from '../constants.ts';
import { idSchema } from './common.ts';
import { fitSchema } from './playlist.ts';
import { overlayOpacitySchema, overlayPositionSchema, overlaySizePctSchema } from './overlay.ts';
import { genAspectSchema } from './template.ts';
import { reportFreqSchema, reportMetricSchema } from './report-schedule.ts';
import { layoutPresetSchema } from './layout.ts';

// Assistant tool input schemas (docs/05 §Tool set). These are the SINGLE source of
// truth for both the REST-facing services and the assistant/MCP tool definitions:
// each tool's JSON Schema is derived from the matching zod schema here, so the tool
// surface can never drift from what the services accept. Kept free of `.refine()`
// so `z.toJSONSchema` produces a clean object schema for the model.

// 'YYYY-MM-DD' in the screen's timezone; 'HH:MM' 24h; days bitmask (bit 0=Mon…6=Sun).
const dateStringSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const timeStringSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM');
const daysMaskSchema = z.number().int().min(0).max(127);

const playlistItemInputSchema = z.object({
  media_id: idSchema.describe('A media id returned by search_media in this conversation.'),
  duration_ms: z.number().int().positive().describe('How long the item shows, in milliseconds.'),
  fit: fitSchema.default('contain'),
});

// --- Read tools -----------------------------------------------------------------

export const listScreensToolSchema = z.object({
  query: z.string().optional().describe('Filter by screen name (case-insensitive substring).'),
  group: z.string().optional().describe('Filter to screens in a group with this name.'),
  status: z.enum(['online', 'offline', 'never_paired']).optional(),
});

export const listGroupsToolSchema = z.object({
  query: z.string().optional().describe('Filter by group name (case-insensitive substring).'),
});

export const searchMediaToolSchema = z.object({
  query: z.string().optional().describe('Filter by filename (case-insensitive substring).'),
  kind: z.enum(MEDIA_KINDS).optional(),
});

export const listPlaylistsToolSchema = z.object({
  query: z.string().optional().describe('Filter by playlist name (case-insensitive substring).'),
});

export const listSchedulesToolSchema = z.object({
  screen_id: idSchema.optional().describe('Only schedules targeting this screen.'),
  group_id: idSchema.optional().describe('Only schedules targeting this group.'),
});

export const previewScheduleToolSchema = z.object({
  screen_id: idSchema,
  at: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Instant to resolve, unix epoch ms. Defaults to now.'),
});

export const getOrgLimitsToolSchema = z.object({});

export const getBrandKitToolSchema = z.object({});

// get_capabilities: a self-describing catalog of every operation the assistant can run,
// generated from the tool registry (no args). Auto-run so the model knows its full surface.
export const getCapabilitiesToolSchema = z.object({});

// --- Write tools (staged — never executed directly) -----------------------------

export const createPlaylistToolSchema = z.object({
  name: z.string().min(1),
  items: z
    .array(playlistItemInputSchema)
    .default([])
    .describe('Ordered items to place in the new playlist.'),
});

export const updatePlaylistItemsToolSchema = z.object({
  playlist_id: idSchema,
  items: z.array(playlistItemInputSchema).describe('Full replacement of the playlist items.'),
});

const scheduleTargetSchema = z.object({
  type: z.enum(TARGET_TYPES),
  id: idSchema.optional().describe('Screen or group id. Omit only when type is "org".'),
});

const scheduleWindowSchema = z.object({
  days_mask: daysMaskSchema.default(127).describe('Bitmask, bit 0=Mon … 6=Sun. 127 = every day.'),
  start_time: timeStringSchema.nullish().describe('HH:MM, screen-local. Null = all day.'),
  end_time: timeStringSchema.nullish(),
  start_date: dateStringSchema.nullish().describe('YYYY-MM-DD. Null = starting now.'),
  end_date: dateStringSchema.nullish().describe('YYYY-MM-DD. Null = indefinitely.'),
  priority: z.number().int().default(0),
});

export const createScheduleToolSchema = z.object({
  playlist_id: idSchema,
  target: scheduleTargetSchema,
  // When the model omits the window, default to every day / all day / now →
  // indefinitely. The default is a complete object (the two non-nullable fields) so
  // it matches the schema's output type; the nullable time/date fields stay null.
  window: scheduleWindowSchema.default({ days_mask: 127, priority: 0 }),
  // Sprint 10: per-schedule layout override id (org-owned; null = none). Ids only, same
  // convention as playlist_id/target.id above — the model resolves a layout name to an
  // id via list_layouts before calling this tool.
  layout_id: idSchema.nullish().describe('Layout id to override the screen\'s own layout while this schedule wins. Omit/null for none.'),
});

export const updateScheduleToolSchema = z.object({
  schedule_id: idSchema,
  patch: z.object({
    playlist_id: idSchema.optional(),
    target: scheduleTargetSchema.optional(),
    days_mask: daysMaskSchema.optional(),
    start_time: timeStringSchema.nullish(),
    end_time: timeStringSchema.nullish(),
    start_date: dateStringSchema.nullish(),
    end_date: dateStringSchema.nullish(),
    priority: z.number().int().optional(),
    enabled: z.boolean().optional(),
    layout_id: idSchema.nullish().describe('Set/clear the per-schedule layout override.'),
  }),
});

export const deleteScheduleToolSchema = z.object({ schedule_id: idSchema });

export const createGroupToolSchema = z.object({
  name: z.string().min(1),
  screen_ids: z.array(idSchema).default([]),
});

export const renameScreenToolSchema = z.object({
  screen_id: idSchema,
  name: z.string().min(1),
});

// --- Brand kit + overlays (docs/09 §C2/§D) --------------------------------------

export const updateBrandKitToolSchema = z.object({
  logo_media_id: idSchema
    .nullish()
    .describe('A media id from search_media (an image). Null clears the brand logo.'),
  colors: z
    .array(z.string())
    .max(6)
    .optional()
    .describe('Up to 6 brand colors as #RGB / #RRGGBB hex strings.'),
  font: z.string().nullish().describe('Brand font family name. Null clears it.'),
  tone: z.string().nullish().describe('Tone-of-voice line for generated copy. Null clears it.'),
});

// Inline overlay definition — creates a new overlay when no existing one is named.
const overlayInlineSchema = z.object({
  name: z.string().optional().describe('Optional name for the new overlay.'),
  logo_media_id: idSchema
    .nullish()
    .describe('Image media id for the logo watermark. Omit/null for a ticker-only overlay.'),
  position: overlayPositionSchema.default('br').describe('Logo corner: br|bl|tr|tl.'),
  size_pct: overlaySizePctSchema.default(10).describe('Logo width as % of screen width (3..30).'),
  opacity: overlayOpacitySchema.default(90).describe('Logo opacity % (10..100).'),
  ticker_text: z.string().nullish().describe('Persistent ticker strip text. Omit for no ticker.'),
});

export const setOverlayToolSchema = z.object({
  target: z
    .object({
      scope: z
        .enum(['screen', 'org-default'])
        .describe('"screen" assigns to one screen; "org-default" sets the org-wide default.'),
      screen: z
        .string()
        .optional()
        .describe('Screen name or id — required when scope is "screen".'),
    })
    .describe('Where the overlay applies.'),
  overlay: z
    .string()
    .optional()
    .describe('An existing overlay name or id to assign. Provide this OR `inline`.'),
  inline: overlayInlineSchema
    .optional()
    .describe('A new overlay to create and assign. Provide this OR `overlay`.'),
});

// set_group_overlay: assign/clear the overlay for a whole screen group (Sprint 4). Provide
// exactly one of `overlay` (existing), `inline` (new), or `clear:true` (remove the overlay).
export const setGroupOverlayToolSchema = z.object({
  group: z.string().min(1).describe('Group name or id to set the overlay on.'),
  overlay: z
    .string()
    .optional()
    .describe('An existing overlay name or id to assign to the group.'),
  inline: overlayInlineSchema
    .optional()
    .describe('A new overlay to create and assign to the group.'),
  clear: z.boolean().optional().describe('Set true to remove the group overlay.'),
});

// add_media_to_playlists: append media (by name or id) to the END of several playlists at
// once (Sprint 4 bulk-add). Each item gets the default duration for its kind. Any name that
// resolves to zero or more than one target aborts with a question — nothing is staged.
export const addMediaToPlaylistsToolSchema = z.object({
  media: z
    .array(z.string().min(1))
    .min(1)
    .describe('Media filenames or ids to add.'),
  playlists: z
    .array(z.string().min(1))
    .min(1)
    .describe('Playlist names or ids to add the media to.'),
});

// --- Content generation tools (docs/09 §B2/§B3/§B4/E) ---------------------------

// generate_image: standalone image generation. Executes immediately (metered) and saves
// a draft; the staged action is "save to library". aspect defaults to the slide canvas.
export const generateImageToolSchema = z.object({
  prompt: z.string().min(1).describe('What to generate. Brand palette/logo hints are added automatically.'),
  aspect: genAspectSchema.default('16:9').describe('Image aspect ratio.'),
});

// generate_slide: pick/fill a template into a draft html_template, optional generated image.
export const generateSlideToolSchema = z.object({
  brief: z.string().min(1).describe('What the slide should say/show, in plain language.'),
  template_id: z
    .string()
    .optional()
    .describe('Template id to use. Omit to let the assistant choose the best fit.'),
  media_ids: z
    .array(idSchema)
    .default([])
    .describe('Existing image media ids to place in the slide (e.g. a product photo).'),
});

// generate_snippet: the assistant writes a small self-contained HTML/CSS/JS block.
export const generateSnippetToolSchema = z.object({
  brief: z.string().min(1).describe('What the snippet does (countdown, ticker, QR block, …).'),
});

// update_template_fields: change field values on an existing html_template media, no regen.
export const updateTemplateFieldsToolSchema = z.object({
  media: z.string().min(1).describe('The html_template media name or id to edit.'),
  fields: z
    .record(z.string(), z.string())
    .describe('Field key → new value map (e.g. {"price":"12.99"}). Only the given keys change.'),
});

// --- Analytics + reports + snapshots (Sprint 8, docs/05 §Tool set additions) ----

// A scope naming an org (default), a group, or a screen — `ref` is a name or id the
// tool resolves server-side (same "name or id" convention as set_group_overlay), so
// the model can pass what the live inventory digest gave it without a lookup round-trip.
export const analyticsScopeToolSchema = z.object({
  type: z.enum(TARGET_TYPES).default('org').describe('"org" (default), "group", or "screen".'),
  ref: z
    .string()
    .optional()
    .describe('Group or screen name/id. Required unless type is "org".'),
});

export const analyticsPeriodToolSchema = z
  .union([
    z.enum(['today', 'yesterday', '7d', '30d']),
    z.object({ from: dateStringSchema, to: dateStringSchema }).describe('Explicit inclusive range.'),
  ])
  .default('7d');

// get_analytics: playback + uptime summary the model narrates ("how did X perform").
export const getAnalyticsToolSchema = z.object({
  scope: analyticsScopeToolSchema.default({ type: 'org' }),
  period: analyticsPeriodToolSchema,
});

// request_screen_snapshot: ask a screen to capture what it's showing right now.
// Auto-apply tier (docs/05 §Change plan tiers) — there is nothing to undo.
export const requestScreenSnapshotToolSchema = z.object({
  screen: z.string().min(1).describe('Screen name or id to snapshot.'),
});

// get_latest_snapshot: metadata for the most recent capture(s) of a screen.
export const getLatestSnapshotToolSchema = z.object({
  screen: z.string().min(1).describe('Screen name or id.'),
});

// create_report_schedule / delete_report_schedule: staged (normal card, not destructive —
// team-lead ruling: a scheduled report is low-stakes even though delete is usually red).
export const createReportScheduleToolSchema = z.object({
  name: z.string().min(1).max(200),
  scope: analyticsScopeToolSchema.default({ type: 'org' }),
  freq: reportFreqSchema,
  day_of_week: z
    .number()
    .int()
    .min(0)
    .max(6)
    .nullish()
    .describe('0=Mon…6=Sun. Required when freq is "weekly".'),
  hour: z.number().int().min(0).max(23).default(9).describe('Hour of day, in `timezone`.'),
  timezone: z.string().min(1).describe('IANA timezone, e.g. "America/Los_Angeles".'),
  metrics: z.array(reportMetricSchema).min(1).describe('One or more of: uptime, playback, top_media.'),
  slack_target: z
    .string()
    .nullish()
    .describe('Slack channel name/id to post to. Omit/null to DM the requester.'),
});

export const listReportSchedulesToolSchema = z.object({});

export const deleteReportScheduleToolSchema = z.object({
  report_schedule: z.string().min(1).describe('Report schedule name or id to delete.'),
});

// --- Layouts + live screen commands (Sprint 9, docs/09 §C1) --------------------

// create_layout: name a preset + assign a playlist to each of its non-main zones. Staged
// (structural create, no plan) — assigning it to a screen is the live-affecting step.
export const createLayoutToolSchema = z.object({
  name: z.string().min(1),
  preset: layoutPresetSchema.describe(
    'fullscreen (default, no zones) | main_ticker (bottom strip) | main_side (L/R panel) | ' +
      'l_shape (side + bottom).',
  ),
  zones: z
    .object({
      side: z.string().optional().describe('Playlist name or id for the side zone.'),
      ticker: z.string().optional().describe('Playlist name or id for the ticker zone.'),
    })
    .default({})
    .describe('Playlist per non-main zone — only the keys the chosen preset uses. Video is ' +
      'only allowed in the main zone; a video in a side/ticker playlist is rejected.'),
});

// set_layout: assign (or clear, via layout:null) a named layout on one screen. Blast-radius
// staged with a change-plan (before→after layout + affected screen + live-now badge) — a
// layout always affects what's on screen right now, unlike a scheduled window.
export const setLayoutToolSchema = z.object({
  screen: z.string().min(1).describe('Screen name or id.'),
  layout: z
    .string()
    .nullable()
    .describe('Layout name or id to assign. Pass null to clear back to fullscreen.'),
});

// refresh_screen_content: ask a screen to re-pull its manifest now instead of waiting for the
// next poll. Auto-apply tier (docs/05 §Change plan tiers) — no visual interruption, no undo.
export const refreshScreenContentToolSchema = z.object({
  screen: z.string().min(1).describe('Screen name or id.'),
});

// reload_screen_player: hard-reload the player app on a screen. Staged (ordinary card, not
// destructive) — the screen visibly blinks, so the user sees that before it happens.
export const reloadScreenPlayerToolSchema = z.object({
  screen: z.string().min(1).describe('Screen name or id.'),
});

// get_screen_status: read-only merge of DB screen info with live hub presence (when
// connected) — online/last_seen/now_playing/layout/overlay.
export const getScreenStatusToolSchema = z.object({
  screen: z.string().min(1).describe('Screen name or id.'),
});

// list_layouts (Sprint 10): the id-lookup counterpart to create_layout/set_layout, so the
// model can resolve a layout name to an id before passing layout_id to create_schedule /
// update_schedule (docs/02 §Layouts) without ever fabricating one (docs/05 rule #2).
export const listLayoutsToolSchema = z.object({
  query: z.string().optional().describe('Filter by layout name (case-insensitive substring).'),
});

// --- Broadcast takeover (Sprint 10, docs/03 §Broadcast) --------------------------

export const BROADCAST_DURATIONS = ['30m', '1h', '4h', 'until_cancelled'] as const;
export const broadcastDurationSchema = z.enum(BROADCAST_DURATIONS);
export type BroadcastDuration = z.infer<typeof broadcastDurationSchema>;

// broadcast_message: an org-wide takeover — every screen shows this message (optionally
// with existing media behind it) immediately, overriding whatever is currently
// scheduled, until cancelled or the duration elapses (services/broadcast.ts#startBroadcast
// implements it as a kind='broadcast', top-priority, org-wide schedule — the resolver
// needs no special-casing). STAGED, ordinary card — NOT destructive-red (fully
// reversible via cancel_broadcast) — but the card's own summary text must be
// unmistakable about the blast radius (every screen, immediately). Editor+, UNRESTRICTED
// only: a scoped member is refused at stage time rather than staging something the
// service would 403 at confirm.
export const broadcastMessageToolSchema = z.object({
  message: z.string().min(1).max(240).describe('The takeover message shown on every screen.'),
  duration: broadcastDurationSchema
    .default('until_cancelled')
    .describe(
      '30m | 1h | 4h | until_cancelled (default). How long the takeover runs before it auto-reverts.',
    ),
  media: z
    .string()
    .optional()
    .describe(
      'Existing media name or id to show behind the message. Omit to generate a quick announcement slide from the message text.',
    ),
});

// cancel_broadcast: end the current org-wide takeover early, restoring normal scheduled
// programming. AUTO-APPLY, NO undo (docs/05 §Change plan tiers) — cancelling a takeover
// restores whatever the regular schedules already resolve to, which is not a single
// meaningful inverse action to store. No args: there is at most one live broadcast.
export const cancelBroadcastToolSchema = z.object({});

// --- Chat + confirm request bodies (the HTTP surface, docs/03 §Assistant) --------

export const assistantChatSchema = z.object({
  conversation_id: idSchema.optional(),
  message: z.string().min(1),
  media_ids: z.array(idSchema).default([]),
});
export type AssistantChat = z.infer<typeof assistantChatSchema>;

export const assistantConfirmSchema = z.object({
  batch_id: idSchema,
  approve: z.boolean(),
});
export type AssistantConfirm = z.infer<typeof assistantConfirmSchema>;

// POST /v1/assistant/undo — re-apply the inverse of an auto-applied reversible-single
// action (docs/05 tiered confirmation; expert panel move #3). Single-use, 15-min window,
// same-user (the batch row's user scope enforces it).
export const assistantUndoSchema = z.object({
  batch_id: idSchema,
});
export type AssistantUndo = z.infer<typeof assistantUndoSchema>;

// Reversibility class per write tool (expert panel Clash 1 resolution). 'reversible-single'
// tools touch exactly ONE entity and are cheap to invert, so the assistant applies them
// immediately with an undo affordance instead of a confirmation card. Everything else —
// bulk, structural, or destructive — stays 'staged' behind the card. Absent ⇒ 'staged'
// (the safe default: a new/unknown tool never auto-applies).
export const TOOL_RISK_CLASSES = ['reversible-single', 'staged'] as const;
export type ToolRiskClass = (typeof TOOL_RISK_CLASSES)[number];
